/**
 * useUpgradeMetrics Hook
 *
 * Fetches monthly upgrade metrics for the current business.
 * Used by dashboard upgrade card to show potential savings from Pro plan.
 *
 * Metrics include:
 * - GMV (gross merchandise value)
 * - Booking count
 * - Current fees paid
 * - Projected Pro costs
 * - Estimated savings
 *
 * Only shows upgrade card for Free users with meaningful activity (>= 3 bookings, >= $300 GMV, > $10 savings).
 */
import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { format } from 'date-fns';

export interface UpgradeMetrics {
  gmv: number;
  booking_count: number;
  current_plan: string;
  current_fees_paid: number;
  projected_pro_fees: number;
  projected_pro_total: number;
  savings_if_upgraded: number;
  show_upgrade_card: boolean;
}

export function useUpgradeMetrics() {
  const [metrics, setMetrics] = useState<UpgradeMetrics | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchMetrics = async () => {
    try {
      setIsLoading(true);
      setError(null);

      // Get authenticated user
      const { data: { user }, error: authError } = await supabase.auth.getUser();
      if (authError || !user) {
        throw new Error('No authenticated user');
      }

      // Get business profile
      const { data: profile, error: profileError } = await supabase
        .from('business_profiles')
        .select('id')
        .eq('user_id', user.id)
        .maybeSingle();

      if (profileError) {
        throw profileError;
      }

      if (!profile) {
        throw new Error('No business profile found');
      }

      // Calculate current month date range
      const now = new Date();
      const monthStart = format(new Date(now.getFullYear(), now.getMonth(), 1), 'yyyy-MM-dd');
      const monthEnd = format(new Date(now.getFullYear(), now.getMonth() + 1, 1), 'yyyy-MM-dd');

      // Call RPC function to get upgrade metrics
      const { data, error: rpcError } = await supabase.rpc('get_upgrade_metrics', {
        business_id_param: profile.id,
        month_start: monthStart,
        month_end: monthEnd,
      });

      if (rpcError) {
        throw rpcError;
      }

      setMetrics(data as UpgradeMetrics);
    } catch (err) {
      console.error('Error fetching upgrade metrics:', err);
      setError(err instanceof Error ? err.message : 'Failed to fetch upgrade metrics');
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchMetrics();
  }, []);

  return {
    metrics,
    isLoading,
    error,
    refetch: fetchMetrics,
  };
}
